<template>
  <div class="rdd">
    <div class="rdd__bar">
      <div class="rdd__filters">
        <button
          v-for="f in filters"
          :key="f.key"
          type="button"
          class="rdd__chip"
          :class="{ 'rdd__chip--on': status === f.key }"
          @click="setStatus(f.key)"
        >{{ f.label }}</button>
      </div>
      <span v-if="!loading" class="rdd__count">{{ total.toLocaleString() }} {{ total === 1 ? 'recipient' : 'recipients' }}</span>
    </div>

    <div v-if="error" class="alert alert-warning py-2 small mb-2">{{ error }}</div>
    <div v-else-if="loading" class="rdd__empty">Loading recipients…</div>
    <div v-else-if="rows.length === 0" class="rdd__empty">No recipients{{ status === 'all' ? ' yet' : ` matching “${statusLabel}”` }}.</div>

    <div v-else class="rdd__tablewrap">
      <table class="rdd__table">
        <thead>
          <tr>
            <th>Recipient</th>
            <th v-if="showStep">Step</th>
            <th>Activity</th>
            <th class="rdd__when">Last event</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.message_id">
            <td>
              <div class="rdd__who">
                <a v-if="r.prospect_id" :href="`/acquire/prospects/${r.prospect_id}`" class="rdd__name">{{ r.name || r.email }}</a>
                <span v-else class="rdd__name">{{ r.name || r.email }}</span>
                <MusubiStatusPill
                  v-if="r.contact_status && r.contact_status !== 'generic'"
                  :status="r.contact_status"
                  :tone="r.contact_status === 'inferred' ? 'warning' : 'success'"
                  :label="r.contact_status"
                  :title="contactTitle(r)"
                  class="rdd__badge"
                />
              </div>
              <div class="rdd__sub">
                <span>{{ r.email }}</span>
                <span v-if="r.company"> · {{ r.company }}</span>
              </div>
            </td>
            <td v-if="showStep" class="rdd__step">{{ r.step || '—' }}</td>
            <td>
              <span class="rdd__chain">
                <span
                  v-for="s in chainFor(r)"
                  :key="s.key"
                  class="rdd__dot"
                  :class="[`rdd__dot--${s.tone}`, { 'rdd__dot--on': s.at }]"
                  :title="s.at ? `${s.label} — ${fmt(s.at)}` : `${s.label} — not yet`"
                >{{ s.label }}</span>
              </span>
            </td>
            <td class="rdd__when">{{ fmt(lastEvent(r)) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading && totalPages > 1" class="rdd__pager">
      <button type="button" class="btn btn-sm btn-link" :disabled="page <= 1" @click="go(page - 1)">‹ Prev</button>
      <span class="rdd__pageno">Page {{ page }} / {{ totalPages }}</span>
      <button type="button" class="btn btn-sm btn-link" :disabled="page >= totalPages" @click="go(page + 1)">Next ›</button>
    </div>
  </div>
</template>

<script>
/**
 * RecipientDrillDown — paginated, status-filterable list of the individual
 * recipients behind a campaign or pipeline: name/email, prospect link, sent
 * step (drips), and each person's interaction chain (delivered → opened →
 * clicked → replied) with timestamps. Backed by the tenant-scoped
 * /recipients endpoints (recipient intelligence, deliverable 2).
 *
 * Props:
 *   endpoint  — base recipients URL (…/recipients); status + page appended
 *   showStep  — render the "Step" column (drip pipelines only)
 */
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiStatusPill from "@root/components/ui/MusubiStatusPill.vue";

const FILTERS = [
  { key: 'all',     label: 'All' },
  { key: 'opened',  label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'replied', label: 'Replied' },
  { key: 'bounced', label: 'Bounced' },
];

export default {
  name: 'RecipientDrillDown',
  components: { MusubiStatusPill },
  props: {
    endpoint: { type: String, required: true },
    showStep: { type: Boolean, default: false },
  },
  data() {
    return {
      api: useApi(),
      filters: FILTERS,
      status: 'all',
      page: 1,
      rows: [],
      total: 0,
      perPage: 25,
      loading: false,
      error: '',
    };
  },
  computed: {
    totalPages() { return Math.max(1, Math.ceil(this.total / this.perPage)); },
    statusLabel() { return (FILTERS.find(f => f.key === this.status) || {}).label || this.status; },
  },
  watch: {
    endpoint() { this.page = 1; this.status = 'all'; this.load(); },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true; this.error = '';
      try {
        const q = `status=${encodeURIComponent(this.status)}&page=${this.page}`;
        const res = await this.api.get(`${this.endpoint}?${q}`);
        // useApi() already unwraps the axios envelope (ApiResponseProcessor's
        // interceptor resolves to `response.data` directly), so `res` here IS
        // the controller's JSON body {data, total, page, per_page} — NOT an
        // axios response whose own `.data` holds that body. Only step into
        // `res.data` when it's genuinely a nested wrapper (i.e. still has its
        // own `.data`); otherwise `res` is already the object we want. Getting
        // this wrong silently drops `res.data` (the recipients array) one
        // level too deep, so `body.data` reads as undefined and the panel
        // renders "No recipients yet" even though the API returned rows.
        const body = res?.data?.data !== undefined ? res.data : res;
        this.rows = Array.isArray(body?.data) ? body.data : [];
        this.total = body?.total ?? this.rows.length;
        this.perPage = body?.per_page ?? this.perPage;
      } catch (e) {
        this.error = e?.response?.status === 403
          ? 'Recipient detail needs Outreach access.'
          : (e?.response?.data?.error || e?.message || 'Could not load recipients.');
        this.rows = []; this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    setStatus(k) { if (this.status === k) return; this.status = k; this.page = 1; this.load(); },
    go(p) { if (p < 1 || p > this.totalPages) return; this.page = p; this.load(); },
    chainFor(r) {
      return [
        { key: 'delivered', label: 'D', tone: 'ok',     at: r.delivered_at },
        { key: 'opened',    label: 'O', tone: 'engage', at: r.opened_at },
        { key: 'clicked',   label: 'C', tone: 'engage', at: r.clicked_at },
        { key: 'replied',   label: 'R', tone: 'reply',  at: r.replied_at },
        ...(r.bounced_at || r.complained_at ? [{ key: 'bounced', label: 'B', tone: 'warn', at: r.bounced_at || r.complained_at }] : []),
      ];
    },
    lastEvent(r) {
      return [r.replied_at, r.clicked_at, r.opened_at, r.bounced_at, r.complained_at, r.delivered_at, r.sent_at]
        .filter(Boolean).sort((a, b) => b - a)[0] || null;
    },
    contactTitle(r) {
      const c = r.contact_confidence != null ? ` (confidence ${r.contact_confidence}%)` : '';
      return {
        verified: 'Mailbox verified',
        found: "Published on the company's own website",
        inferred: `Inferred from the firm's email pattern${c}`,
      }[r.contact_status] || r.contact_status;
    },
    fmt(ts) { return ts ? new Date(ts * 1000).toLocaleString() : '—'; },
  },
};
</script>

<style scoped>
.rdd { margin-top: 4px; }
.rdd__bar { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; flex-wrap: wrap; }
.rdd__filters { display: flex; gap: 6px; flex-wrap: wrap; }
.rdd__chip { font: inherit; font-size: 0.74rem; font-weight: 600; padding: 3px 10px; border-radius: 999px; border: 1px solid var(--m-border, #E4E7F5); background: var(--m-bg-content, #F7F8FC); color: var(--m-text-secondary, #5A6072); cursor: pointer; }
.rdd__chip:hover { border-color: var(--m-border-strong, #C7CBE0); }
.rdd__chip--on { background: var(--m-accent-soft, #EEF0FF); border-color: var(--m-accent, #4F46E5); color: var(--m-accent, #4F46E5); }
.rdd__count { margin-left: auto; font-size: 0.74rem; color: var(--m-text-tertiary, #9AA0AC); }
.rdd__empty { color: var(--m-text-secondary, #5A6072); font-size: 0.84rem; background: var(--m-bg-content, #F7F8FC); border: 1px dashed var(--m-border-strong, #C7CBE0); border-radius: var(--m-radius, 8px); padding: 12px; }

.rdd__tablewrap { overflow-x: auto; }
.rdd__table { width: 100%; border-collapse: collapse; }
.rdd__table th { text-align: left; font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary, #9AA0AC); font-weight: 700; padding: 6px 8px; border-bottom: 1px solid var(--m-border, #E4E7F5); }
.rdd__table td { padding: 8px; border-bottom: 1px solid var(--m-border, #E4E7F5); vertical-align: top; font-size: 0.82rem; }
.rdd__who { display: flex; align-items: center; gap: 6px; }
.rdd__name { font-weight: 650; color: var(--m-text-primary, #1A1D29); text-decoration: none; }
.rdd__name:hover { color: var(--m-accent, #4F46E5); text-decoration: underline; }
.rdd__badge { font-size: 0.62rem; }
.rdd__sub { font-size: 0.72rem; color: var(--m-text-tertiary, #9AA0AC); margin-top: 2px; word-break: break-word; }
.rdd__step { font-size: 0.76rem; color: var(--m-text-secondary, #5A6072); max-width: 220px; }
.rdd__when { white-space: nowrap; font-size: 0.74rem; color: var(--m-text-tertiary, #9AA0AC); }

.rdd__chain { display: inline-flex; gap: 4px; }
.rdd__dot { width: 20px; height: 20px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; font-size: 0.64rem; font-weight: 800; background: var(--m-bg-content, #F7F8FC); border: 1px solid var(--m-border, #E4E7F5); color: var(--m-text-tertiary, #9AA0AC); }
.rdd__dot--on.rdd__dot--ok { background: var(--m-success-soft, #D1FAE5); border-color: var(--m-success, #0D9488); color: var(--m-success, #0D9488); }
.rdd__dot--on.rdd__dot--engage { background: var(--m-accent-soft, #EEF0FF); border-color: var(--m-accent, #4F46E5); color: var(--m-accent, #4F46E5); }
.rdd__dot--on.rdd__dot--reply { background: #F3E8FF; border-color: #7C3AED; color: #7C3AED; }
.rdd__dot--on.rdd__dot--warn { background: var(--m-warning-soft, #FEF3C7); border-color: var(--m-status-warning, #D97706); color: var(--m-status-warning, #D97706); }

.rdd__pager { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 10px; }
.rdd__pageno { font-size: 0.76rem; color: var(--m-text-tertiary, #9AA0AC); }
</style>
